import { useState } from "react";
import { HighlightOutlined } from "@ant-design/icons";
import {
  App,
  Button,
  Descriptions,
  Flex,
  Spin,
  Tag,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import GEmpty from "@/styles/Placeholder/GEmpty";
import { CommonStateEnum } from "@/types/common";
import {
  EquipmentCategory,
  EquipmentCategoryParams,
} from "@/types/equipment";
import { CategoryDetailPanel } from "../../style";

// 当前类目支持独立修改的字段。
type EditableCategoryField = "name" | "code" | "desc";

// 设备类目详情组件参数
interface CategoryDetailProps {
  category?: EquipmentCategory; // 当前选中的类目
  loading: boolean; // 类目详情加载状态
  actionLoading: boolean; // 类目操作提交状态
  onAddChild: () => void; // 新增子类目
  onEdit: (body: EquipmentCategoryParams) => Promise<boolean>; // 编辑当前类目字段
  onSetEnabled: (enabled: boolean) => Promise<void>; // 更新类目启用状态
  canEdit: boolean; // 是否允许新增/编辑设备类目
}

// 展示当前类目详情并提供新增、单字段编辑和启停操作
const CategoryDetail = ({
  category,
  loading,
  actionLoading,
  onAddChild,
  onEdit,
  onSetEnabled,
  canEdit,
}: CategoryDetailProps) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  // 统一记录正在编辑的字段，避免多个内联输入框同时处于编辑状态。
  const [editingField, setEditingField] =
    useState<EditableCategoryField>();

  if (loading) {
    return (
      <CategoryDetailPanel $fill>
        <Spin />
      </CategoryDetailPanel>
    );
  }

  if (!category) {
    return (
      <CategoryDetailPanel $fill>
        <GEmpty description={t("equipment.selectCategoryPlaceholder")} />
      </CategoryDetailPanel>
    );
  }

  // 类目状态决定状态标签及启停操作方向
  const enabled = category.state === CommonStateEnum.ACTIVE;

  // 内容未变化时只退出编辑，不发起请求。
  const handleEdit = async (
    field: EditableCategoryField,
    value: string,
  ) => {
    // 禁用类目不接受信息修改，避免状态变化期间继续提交编辑请求。
    if (!canEdit || !enabled) {
      setEditingField(undefined);
      return;
    }

    const normalizedValue = value.trim();
    const currentValue = (category[field] ?? "").trim();
    if (normalizedValue === currentValue) {
      setEditingField(undefined);
      return;
    }
    // 名称和编号为必填项，清空时不提交并保留输入内容。
    if (field !== "desc" && !normalizedValue) return;

    // 每次只替换当前字段，其他字段继续使用当前类目详情中的值。
    const succeeded = await onEdit({
      code: field === "code" ? normalizedValue : category.code,
      name: field === "name" ? normalizedValue : category.name,
      desc: field === "desc"
        ? normalizedValue || null
        : category.desc,
    });
    // 请求成功后退出编辑；失败时保留编辑状态和输入内容，便于继续修改。
    if (succeeded) setEditingField(undefined);
  };

  // 二次确认后切换当前类目的启用状态
  const confirmStateChange = () => {
    modal.confirm({
      title: enabled
        ? t("equipment.disableCategory")
        : t("equipment.enableCategory"),
      content: enabled
        ? t("equipment.confirmDisableCategory")
        : t("equipment.confirmEnableCategory"),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      onOk: () => onSetEnabled(!enabled),
    });
  };

  return (
    <CategoryDetailPanel>
      <Flex align="center" gap={8} wrap="wrap">
        {/* 类目名称沿用商品类目管理的文字内联编辑交互。 */}
        <Typography.Title
          level={4}
          editable={canEdit && enabled
            ? {
              editing: editingField === "name",
              text: category.name,
              maxLength: 100,
              icon: <HighlightOutlined />,
              tooltip: t("product.clickModify"),
              onStart: () => setEditingField("name"),
              onCancel: () => setEditingField(undefined),
              onChange: value => void handleEdit("name", value),
            }
            : false}
          style={{ margin: 0 }}
        >
          {category.name}
        </Typography.Title>
        <Tag color={enabled ? "success" : "error"}>
          {enabled ? t("equipment.enabled") : t("equipment.disabled")}
        </Tag>
        {canEdit && (
          <>
            <Button
              disabled={!enabled || actionLoading || Boolean(editingField)}
              onClick={onAddChild}
            >
              {t("equipment.addSubCategory")}
            </Button>
            <Button
              type={enabled ? "default" : "primary"}
              danger={enabled}
              loading={actionLoading}
              disabled={Boolean(editingField)}
              onClick={confirmStateChange}
            >
              {enabled
                ? t("equipment.disableCategory")
                : t("equipment.enableCategory")}
            </Button>
          </>
        )}
      </Flex>
      <Descriptions
        bordered
        size="small"
        column={1}
        labelStyle={{ width: 100 }}
      >
        <Descriptions.Item label={t("equipment.categoryCode")}>
          {/* 编号与名称相互独立，提交时只更新编号字段。 */}
          <Typography.Text
            editable={canEdit && enabled
              ? {
                editing: editingField === "code",
                text: category.code,
                maxLength: 100,
                icon: <HighlightOutlined />,
                tooltip: t("product.clickModify"),
                onStart: () => setEditingField("code"),
                onCancel: () => setEditingField(undefined),
                onChange: value => void handleEdit("code", value),
              }
              : false}
          >
            {category.code}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={t("equipment.categoryDesc")}>
          {/* 描述允许清空，空字符串在提交时统一转换为 null。 */}
          <Typography.Paragraph
            editable={canEdit && enabled
              ? {
                editing: editingField === "desc",
                text: category.desc ?? "",
                maxLength: 1000,
                autoSize: { minRows: 2, maxRows: 5 },
                icon: <HighlightOutlined />,
                tooltip: t("product.clickModify"),
                onStart: () => setEditingField("desc"),
                onCancel: () => setEditingField(undefined),
                onChange: value => void handleEdit("desc", value),
              }
              : false}
            style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}
          >
            {category.desc || "-"}
          </Typography.Paragraph>
        </Descriptions.Item>
      </Descriptions>
    </CategoryDetailPanel>
  );
};

export default CategoryDetail;
